import { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Sentry } from "@/lib/sentry";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Top-level error boundary. Catches anything a descendant throws during
 * render/lifecycle so a single bad component can't blank the whole app.
 *
 * The fallback is intentionally plain, it has to render without any of
 * the providers further up the tree being healthy.
 */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    Sentry.withScope((scope) => {
      scope.setContext("react", { componentStack: info.componentStack });
      Sentry.captureException(error);
    });
    console.error("Uncaught render error:", error, info.componentStack);
  }

  private reload = () => {
    window.location.reload();
  };

  private goHome = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.error) {
      return (
        <main
          role="alert"
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "2rem",
            fontFamily: "system-ui, -apple-system, sans-serif",
            background: "#faf9f7",
          }}
        >
          <div style={{ maxWidth: 480, textAlign: "center" }}>
            <h1 style={{ fontSize: "1.5rem", marginBottom: "0.75rem" }}>
              Something went wrong.
            </h1>
            <p style={{ color: "#555", marginBottom: "1.5rem", lineHeight: 1.5 }}>
              The page hit an unexpected error. Reloading usually fixes it. If
              it keeps happening, let us know at team@lembra-celebrations.com.
            </p>
            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center" }}>
              <Button onClick={this.reload}>Reload page</Button>
              <Button variant="outline" onClick={this.goHome}>
                Back to home
              </Button>
            </div>
          </div>
        </main>
      );
    }
    return this.props.children;
  }
}
